import React, { useEffect, useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import Loading from "../../../../../components/general/Loading";
import {
  handleError,
  isNotBusinessError,
} from "../../../../../utils/handleError";
import {
  getIndicatorCandidatureChangesByState,
  getIndicatorCandidatureChangesByType,
} from "../../../../../rest/indicator";
import ExportDialog from "../utils/ExportDialog";
import LastUpdate from "../utils/LastUpdate";
import * as XLSX from "xlsx";
import { buildWorkbookApplicationChanges } from "../sectionsIndicatorsCandidatures/exporters/exporterApplicationChanges";
import { useClientPdf } from "../utils/useClientPdf";

export default function IndicatorApplicationChanges() {
  const intl = useIntl();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dataStates, setDataStates] = useState([]);
  const [dataTypes, setDataTypes] = useState([]);

  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(null);

  const [showExport, setShowExport] = useState(false);

  const { pdfRef, exportPdf } = useClientPdf({
    marginMm: 10,
    orientation: "landscape",
    hideSelectors: ['[data-hide-on-export="true"]', ".modal", ".toast"],
  });

  const toIso = (ts) => {
    if (ts == null) return null;
    if (typeof ts === "number") return new Date(ts).toISOString();
    if (typeof ts === "string") {
      const n = Date.parse(ts);
      return Number.isNaN(n) ? null : new Date(n).toISOString();
    }
    return null;
  };

  const epochMs = (iso) => {
    if (!iso) return NaN;
    const n = Date.parse(iso);
    return Number.isNaN(n) ? NaN : n;
  };

  const extract = (resp) => {
    const p = resp?.data ?? {};
    const rows = Array.isArray(p) ? p : p.data ?? [];
    const tsIso = Array.isArray(p) ? null : toIso(p.lastRefreshedAt);
    const ver = Array.isArray(p) ? null : p.refreshVersion ?? null;
    return { rows, ts: tsIso, ver };
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [resStates, resTypes] = await Promise.all([
          getIndicatorCandidatureChangesByState(),
          getIndicatorCandidatureChangesByType(),
        ]);

        const ps = extract(resStates);
        const pt = extract(resTypes);

        setDataStates(ps.rows || []);
        setDataTypes(pt.rows || []);

        const es = epochMs(ps.ts);
        const et = epochMs(pt.ts);

        if (!Number.isNaN(es) && (Number.isNaN(et) || es >= et)) {
          setLastUpdate(ps.ts);
          setRefreshVersion(ps.ver);
        } else if (!Number.isNaN(et)) {
          setLastUpdate(pt.ts);
          setRefreshVersion(pt.ver);
        } else {
          setLastUpdate(null);
          setRefreshVersion(null);
        }
      } catch (err) {
        if (isNotBusinessError(err)) setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const nf = new Intl.NumberFormat("pt-PT");
  const toPct = (v, t) => (t > 0 ? Math.round((v / t) * 100) : 0);

  const FALLBACK_LABELS = {
    APPROVED: "Aprovada",
    VALIDATION: "A aguardar decisão do IEFP",
    WAIT_CONTESTATION: "Em audiência prévia",
    CONTESTATION_ANALYSIS: "Em análise de audiência prévia",
    REJECTED: "Indeferida",
    WAIT_TERM_ACCEPT_SUBMISSION: "A aguardar Termo de Aceitação",
    NIB: "NIB",
    POSTOS_TRABALHO: "Postos de trabalho",
    FINANCIAMENTO: "Investimento",
    OUTRO: "Outro",
  };

  const TYPE_COLORS = {
    NIB: "#6f42c1",
    POSTOS_TRABALHO: "#0dcaf0",
    FINANCIAMENTO: "#ffc107",
    OUTRO: "#adb5bd",
  };
  const STATE_COLORS = {
    APPROVED: "#198754",
    VALIDATION: "#6c757d",
    WAIT_CONTESTATION: "#fd7e14",
    CONTESTATION_ANALYSIS: "#20c997",
    REJECTED: "#dc3545",
    WAIT_TERM_ACCEPT_SUBMISSION: "#0d6efd",
  };

  const rowsTypesOrdered = useMemo(() => {
    const arr = (dataTypes || []).map((i) => ({
      code: i.code ?? "-",
      counter: Number(i.counter) || 0,
    }));
    arr.sort(
      (a, b) =>
        b.counter - a.counter || String(a.code).localeCompare(String(b.code))
    );
    return arr;
  }, [dataTypes]);

  const rowsStatesOrdered = useMemo(() => {
    const arr = (dataStates || []).map((i) => ({
      code: i.code ?? "-",
      counter: Number(i.counter) || 0,
    }));
    arr.sort(
      (a, b) =>
        b.counter - a.counter || String(a.code).localeCompare(String(b.code))
    );
    return arr;
  }, [dataStates]);

  const totalTypes = useMemo(
    () => rowsTypesOrdered.reduce((s, r) => s + r.counter, 0),
    [rowsTypesOrdered]
  );
  const totalStates = useMemo(
    () => rowsStatesOrdered.reduce((s, r) => s + r.counter, 0),
    [rowsStatesOrdered]
  );

  if (loading) return <Loading />;
  if (error) return handleError(error);

  const handleExportExcelFinal = async () => {
    try {
      const wb = await buildWorkbookApplicationChanges();
      if (!wb || !wb.SheetNames) throw new Error("Workbook vazio");
      XLSX.writeFile(wb, "indicadores_alteracoes_aplicacao.xlsx");
      setShowExport(false);
    } catch (e) {
      console.error("[Excel Alterações] falhou:", e);
      alert("Falha ao gerar Excel: " + (e?.message || e));
    }
  };

  const handleExportPdfClient = async () => {
    try {
      await exportPdf("indicadores_alteracoes_aplicacao.pdf");
      setShowExport(false);
    } catch (e) {
      console.error("[PDF Alterações] falhou:", e);
      alert("Falha ao gerar PDF: " + (e?.message || e));
    }
  };

  const ListGroupCard = ({
    titleId,
    titleDefault,
    rows,
    total,
    i18nPrefix,
    colors,
  }) => (
    <div className="card h-100">
      <div className="card-header fw-semibold d-flex justify-content-between align-items-center">
        <span>
          <FormattedMessage id={titleId} defaultMessage={titleDefault} />
        </span>
        <span className="text-muted">
          <FormattedMessage id="indicator.total" defaultMessage="Total" />:{" "}
          <strong>{nf.format(total)}</strong>
        </span>
      </div>
      <ul className="list-group list-group-flush">
        {rows.length === 0 && (
          <li className="list-group-item text-muted">Sem dados</li>
        )}
        {rows.map((r) => {
          const label = (
            <FormattedMessage
              id={`${i18nPrefix}.${r.code}`}
              defaultMessage={FALLBACK_LABELS[r.code] || r.code}
            />
          );
          const pct = toPct(r.counter, total);
          return (
            <li key={r.code} className="list-group-item">
              <div className="d-flex justify-content-between">
                <div className="text-truncate">{label}</div>
                <div className="ms-2">
                  <strong>{nf.format(r.counter)}</strong>{" "}
                  <span className="text-muted">({pct}%)</span>
                </div>
              </div>
              <div
                className="progress mt-1"
                style={{ height: 8, backgroundColor: "#e9ecef" }}
                title={`${FALLBACK_LABELS[r.code] || r.code}: ${nf.format(
                  r.counter
                )} (${pct}%)`}
              >
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: colors[r.code] || "#0d6efd",
                  }}
                  aria-valuenow={pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <div className="container-fluid" ref={pdfRef}>
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="text-secondary mb-0">
            <FormattedMessage
              id="indicator.candidatures.byChangeType"
              defaultMessage="Alterações à Candidatura"
            />
          </h4>
        </Col>
        <Col className="text-end">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowExport(true);
            }}
            style={{ color: "#e5533d", textDecoration: "underline" }}
            data-hide-on-export="true"
          >
            <FormattedMessage
              id="indicator.exportar"
              defaultMessage="Exportar"
            />
          </a>
        </Col>
      </Row>

      <Row xs={1} md={2} className="g-3">
        <Col>
          <ListGroupCard
            titleId="indicator.changes.state"
            titleDefault="Estados"
            rows={rowsStatesOrdered}
            total={totalStates}
            i18nPrefix="indicator.state"
            colors={STATE_COLORS}
          />
        </Col>
        <Col>
          <ListGroupCard
            titleId="indicator.changes.type"
            titleDefault="Tipos"
            rows={rowsTypesOrdered}
            total={totalTypes}
            i18nPrefix="indicator.changeType"
            colors={TYPE_COLORS}
          />
        </Col>
      </Row>

      <LastUpdate iso={lastUpdate} version={refreshVersion} className="mt-3" />

      <ExportDialog
        show={showExport}
        onHide={() => setShowExport(false)}
        options={[
          {
            onClick: handleExportExcelFinal,
            filename: "indicadores_alteracoes_aplicacao.xlsx",
          },
          {
            onClick: handleExportPdfClient,
            filename: "indicadores_alteracoes_aplicacao.pdf",
          },
        ]}
      />
    </div>
  );
}